import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../AuthContext";
import CreatePost from "./CreatePost"; // ✅ Correct path to your external CreatePost.js
import EditPost from "./EditPost"; // ✅ Import EditPost component
import { buildApiUrl } from "../../config";  // ✅ Import config

// Module-level image cache: persists across remounts, shared across all carousels
const imageCache = new Set();

// ✅ Image Carousel — defined at module level so it never remounts on parent re-renders
const ImageCarousel = ({ imageUrls, onImageClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const containerRef = React.useRef(null);

  // Intersection Observer for lazy loading
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Preload current image only when visible
  React.useEffect(() => {
    if (!isVisible) return;
    const currentUrl = imageUrls[currentImageIndex];
    const fullUrl = currentUrl?.startsWith('http') ? currentUrl : buildApiUrl(currentUrl);
    if (!fullUrl) return;

    if (imageCache.has(fullUrl)) {
      setImageLoading(false);
      setImageError(false);
      return;
    }

    setImageLoading(true);
    setImageError(false);

    const img = new Image();
    img.onload = () => {
      imageCache.add(fullUrl);
      setImageLoading(false);
      setImageError(false);
      const preloadAdjacent = () => {
        const nextIndex = (currentImageIndex + 1) % imageUrls.length;
        const prevIndex = (currentImageIndex - 1 + imageUrls.length) % imageUrls.length;
        [imageUrls[nextIndex], imageUrls[prevIndex]].forEach(url => {
          if (url) {
            const adjacentUrl = url.startsWith('http') ? url : buildApiUrl(url);
            if (!imageCache.has(adjacentUrl)) {
              const adjacentImg = new Image();
              adjacentImg.src = adjacentUrl;
              adjacentImg.onload = () => imageCache.add(adjacentUrl);
            }
          }
        });
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(preloadAdjacent, { timeout: 200 });
      } else {
        setTimeout(preloadAdjacent, 100);
      }
    };
    img.onerror = () => {
      setImageLoading(false);
      setImageError(true);
    };
    img.src = fullUrl;
  }, [currentImageIndex, imageUrls, isVisible]);

  const nextImage = () => setCurrentImageIndex((prev) => prev + 1 >= imageUrls.length ? 0 : prev + 1);
  const prevImage = () => setCurrentImageIndex((prev) => prev - 1 < 0 ? imageUrls.length - 1 : prev - 1);

  if (!imageUrls || imageUrls.length === 0) return null;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative cursor-pointer min-h-[200px] bg-gray-100 flex items-center justify-center"
        onClick={() => { if (!imageLoading && !imageError) onImageClick(imageUrls[currentImageIndex], imageUrls, currentImageIndex); }}>
        {(imageLoading || !isVisible) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">📸</span>
              </div>
            </div>
          </div>
        )}
        {imageError && isVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center text-gray-500">
              <span className="text-4xl mb-2">🖼️</span>
              <p className="text-sm">Image failed to load</p>
            </div>
          </div>
        )}
        {isVisible && (
          <img
            src={imageUrls[currentImageIndex].startsWith('http') ? imageUrls[currentImageIndex] : buildApiUrl(imageUrls[currentImageIndex])}
            alt={`Post image ${currentImageIndex + 1}`}
            loading="lazy"
            decoding="async"
            className={`w-full h-auto max-h-[600px] object-contain ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          />
        )}
      </div>
      {imageUrls.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full w-8 h-8 flex items-center justify-center transition-opacity z-10">‹</button>
          <button onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full w-8 h-8 flex items-center justify-center transition-opacity z-10">›</button>
        </>
      )}
      {imageUrls.length > 1 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
          {imageUrls.map((_, index) => (
            <button key={index} onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
              className={`w-2 h-2 rounded-full transition-colors ${index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const SchoolPostsTab = ({ 
  studentForm, 
  studentId: propStudentId, 
  authorInfo: propAuthorInfo,
  isMobileMenuOpen: externalMobileMenuOpen,
  setIsMobileMenuOpen: externalSetMobileMenuOpen
}) => {
  const { role, teacherEmail, authorInfo: contextAuthorInfo, school_id } = useAuth();
  
  // ✅ Use prop authorInfo if provided (for students), otherwise use context
  const authorInfo = propAuthorInfo || contextAuthorInfo;
  
  // ✅ Universal user data resolution for all user types
  const getUserData = () => {
    // Priority 1: Explicit authorInfo passed as prop (student case)
    if (propAuthorInfo?.email && propAuthorInfo?.name && propAuthorInfo?.role) {
      return {
        email: propAuthorInfo.email,
        name: propAuthorInfo.name,
        role: propAuthorInfo.role
      };
    }
    
    // Priority 2: Context authorInfo (teacher/parent case)
    if (contextAuthorInfo?.email && contextAuthorInfo?.name && contextAuthorInfo?.role) {
      return {
        email: contextAuthorInfo.email,
        name: contextAuthorInfo.name,
        role: contextAuthorInfo.role
      };
    }
    
    // Priority 3: Teacher-specific data (name will be resolved later)
    if (role === "teacher" && teacherEmail) {
      return {
        email: teacherEmail,
        name: teacherEmail, // Will be resolved by getTeacherName later
        role: "teacher",
        needsNameResolution: true
      };
    }
    
    // Priority 4: LocalStorage fallback
    const lsEmail = localStorage.getItem("userEmail");
    const lsName = localStorage.getItem("userName");
    const lsRole = localStorage.getItem("userRole");
    
    if (lsEmail || lsName || lsRole) {
      return {
        email: lsEmail || "unknown@user.com",
        name: lsName || "Unknown User",
        role: lsRole || role || "student"
      };
    }
    
    // Priority 5: Final fallback
    return {
      email: "fallback@user.com",
      name: "User",
      role: role || "student"
    };
  };

  // ✅ ADD: Get unique student identifier for tagged pupil logic
  const studentId = propStudentId || // Use prop if provided (for parents)
    (role === "student" 
      ? authorInfo?.email || localStorage.getItem("studentEmail") // Use student's email as unique ID
      : teacherEmail); // Fallback for teachers
  
  // ✅ ADD: Backend base URL for file access
  const backendBaseUrl = buildApiUrl("");  // ✅ Use config instead of localhost

  const [activeTab, setActiveTab] = useState("school");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const [savedPosts, setSavedPosts] = useState(new Set()); // ✅ NEW: Track saved posts
  const [showOptionsMenu, setShowOptionsMenu] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [rtlMode, setRtlMode] = useState(false);
  const [postCounters, setPostCounters] = useState({});
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [targetForms, setTargetForms] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [teacherAssignedForms, setTeacherAssignedForms] = useState([]);
  const [staffData, setStaffData] = useState([]);

  // ✅ ADD: Edit post states
  const [editingPost, setEditingPost] = useState(null);
  
  // ✅ NEW: Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);
  const [modalImageLoading, setModalImageLoading] = useState(true);
  const [showEditPost, setShowEditPost] = useState(false);

  // ✅ ADD: Share menu state
  const [showShareMenu, setShowShareMenu] = useState(null);
  const [shareMenuPosition, setShareMenuPosition] = useState({ top: 0, left: 0 });

  // ✅ ADD: Translation state
  const [translatedPosts, setTranslatedPosts] = useState({}); // {postId: {title, content, language}}
  const [translatingPosts, setTranslatingPosts] = useState(new Set()); // Track loading state

  // ✅ FIX: Initialize filter state FROM localStorage using lazy initializer (runs only once)
  const [activeCategory, setActiveCategory] = useState(() => {
    try {
      const saved = localStorage.getItem(`schoolPostsFilters_${studentForm}`);
      if (saved) return JSON.parse(saved).activeCategory || 'all';
    } catch (e) {}
    return 'all';
  });
  const [searchKeyword, setSearchKeyword] = useState(() => {
    try {
      const saved = localStorage.getItem(`schoolPostsFilters_${studentForm}`);
      if (saved) return JSON.parse(saved).searchKeyword || '';
    } catch (e) {}
    return '';
  });
  const [selectedTeacher, setSelectedTeacher] = useState(() => {
    try {
      const saved = localStorage.getItem(`schoolPostsFilters_${studentForm}`);
      if (saved) return JSON.parse(saved).selectedTeacher || 'all';
    } catch (e) {}
    return 'all';
  });
  
  // ✅ ADD: Mobile menu state (use external if provided, otherwise internal)
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const isMobileMenuOpen = externalMobileMenuOpen !== undefined ? externalMobileMenuOpen : internalMobileMenuOpen;
  const setIsMobileMenuOpen = externalSetMobileMenuOpen || setInternalMobileMenuOpen;

  // ✅ FIX: Save filter state to localStorage whenever it changes
  useEffect(() => {
    const filterState = {
      searchKeyword,
      selectedTeacher,
      activeCategory
    };
    localStorage.setItem(`schoolPostsFilters_${studentForm}`, JSON.stringify(filterState));
  }, [searchKeyword, selectedTeacher, activeCategory, studentForm]);

  // ✅ Preload modal images for faster display
  useEffect(() => {
    if (!modalImage) return;

    setModalImageLoading(true);
    const img = new Image();
    img.onload = () => {
      setModalImageLoading(false);
      
      // Preload adjacent images for smooth navigation
      if (modalImages.length > 1) {
        const nextIndex = (modalImageIndex + 1) % modalImages.length;
        const prevIndex = (modalImageIndex - 1 + modalImages.length) % modalImages.length;
        
        [modalImages[nextIndex], modalImages[prevIndex]].forEach(url => {
          if (url) {
            const adjacentImg = new Image();
            adjacentImg.src = url;
          }
        });
      }
    };
    img.onerror = () => {
      setModalImageLoading(false);
    };
    img.src = modalImage;
  }, [modalImage, modalImageIndex, modalImages]);

  // ✅ ADD: Function to get teacher name from email (Memoized)
  const getTeacherName = React.useCallback((email) => {
    // ✅ Add null/undefined checks
    if (!email || typeof email !== 'string') {
      return "Unknown User";
    }
    
    try {
      const staff = staffData.find(s => s.email && s.email.toLowerCase() === email.toLowerCase());
      if (staff) {
        return staff.name;
      }
      // Fallback to parsing email if not found in staff.json
      return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
    } catch (error) {
      console.error("❌ Error in getTeacherName:", error, "Email:", email);
      return "Unknown User";
    }
  }, [staffData]);

  // Resolve teacher names for users that need name resolution (Memoized)
  const resolveTeacherNames = React.useCallback((userData) => {
    if (userData.needsNameResolution) {
      return {
        ...userData,
        name: getTeacherName(userData.email),
        needsNameResolution: false
      };
    }
    return userData;
  }, [getTeacherName]);

  // Get user data and resolve teacher names if needed (Memoized)
  const currentUser = React.useMemo(() => {
    const rawCurrentUser = getUserData();
    return resolveTeacherNames(rawCurrentUser);
  }, [resolveTeacherNames]);
  
  // Commented out to reduce log spam
  // console.log("🔍 SchoolPostsTab user data resolution:", {
  //   propAuthorInfo,
  //   contextAuthorInfo,
  //   role,
  //   teacherEmail,
  //   rawUser: rawCurrentUser,
  //   resolvedUser: currentUser
  // });

  useEffect(() => {
    // ✅ NEW: Fetch staff data for name lookup
    fetch("/staff.json")
      .then(res => res.json())
      .then(data => {
        setStaffData(data);
        // console.log("📋 Loaded staff data:", data.length, "staff members");
      })
      .catch(error => {
        console.error("❌ Error loading staff data:", error);
        setStaffData([]); // Fallback to empty array
      });

    // Fetch all available forms for create post dropdown
    fetch(buildApiUrl("/api/whiteboard/forms"))
      .then((res) => res.json())
      .then((forms) => setTargetForms(forms));

    // Fetch teacher's assigned forms if user is a teacher
    if (role === "teacher") {
      fetch(buildApiUrl("/assignments"))
        .then(res => res.json())
        .then(data => {
          const assignedForms = Object.entries(data)
            .filter(([form, assignment]) => {
              // Handle enhanced format with owners/teachers structure
              if (assignment && typeof assignment === 'object' && !Array.isArray(assignment)) {
                const owners = assignment.owners || [];
                const teachers = assignment.teachers || [];
                return owners.includes(teacherEmail) || teachers.includes(teacherEmail);
              }
              // Handle legacy format (direct array)
              const emailArray = Array.isArray(assignment) ? assignment : 
                                 typeof assignment === 'string' ? [assignment] : [];
              return emailArray.includes(teacherEmail);
            })
            .map(([form]) => form);
          setTeacherAssignedForms(assignedForms);
        })
        .catch(error => {
          console.error("❌ Error fetching teacher assignments:", error);
          setTeacherAssignedForms([]);
        });
    }
  }, [role, teacherEmail]);

  // ✅ Keyboard navigation for image modal
  useEffect(() => {
    const handleKeydown = (e) => {
      if (!showImageModal) return;
      
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [showImageModal, modalImages.length, modalImageIndex]);

  // ✅ Add toast function
  const showNotification = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  // ✅ Add useEffect to track new posts after posts load
  useEffect(() => {
    const lastViewed = localStorage.getItem(`postsLastViewed_${studentForm}`);
    const lastViewedTime = lastViewed ? new Date(lastViewed) : new Date(0);
    const freshPosts = posts.filter((p) => new Date(p.timestamp) > lastViewedTime);

    setHasNewPosts(freshPosts.length > 0);
  }, [posts, studentForm]);

  // Mark posts as viewed when component loads
  useEffect(() => {
    const markAsViewed = () => {
      localStorage.setItem(`postsLastViewed_${studentForm}`, new Date().toISOString());
      setHasNewPosts(false);
    };

    // Mark as viewed after 2 seconds of being on the tab
    const timer = setTimeout(markAsViewed, 2000);
    return () => clearTimeout(timer);
  }, [studentForm]);

  // ✅ NEW: Mark relevant posts as read
  const markRelevantPostsAsRead = async (posts) => {
    try {
      const userEmail = role === "teacher" ? teacherEmail : 
                       role === "parent" ? (authorInfo?.email || localStorage.getItem("userEmail")) : 
                       (authorInfo?.email || localStorage.getItem("userEmail"));
      const userRole = role || 'parent';

      if (!userEmail) return;

      // Filter posts relevant to the user
      const relevantPosts = posts.filter(post => {
        const targetForms = Array.isArray(post.target_forms) ? post.target_forms : [];
        
        // If no target forms, it's for everyone
        if (!targetForms || targetForms.length === 0) return true;
        
        // For parents/students: check if their form is in target_forms
        if (userRole === 'parent' && studentForm) {
          return targetForms.includes(studentForm);
        }
        
        // For teachers: include all posts
        return userRole === 'teacher';
      });

      // Mark each relevant post as read
      for (const post of relevantPosts) {
        const formData = new FormData();
        formData.append('post_id', post.id);
        formData.append('user_email', userEmail);
        formData.append('user_role', userRole);

        await axios.post(buildApiUrl('/api/posts/mark-read'), formData);
      }

      console.log(`✅ Marked ${relevantPosts.length} posts as read for ${userEmail}`);
    } catch (error) {
      console.error("❌ Error marking posts as read:", error);
    }
  };

  // ✅ Enhanced fetchPosts function with likes and comments
  const fetchPosts = async (showLoader = true, isBackgroundRefresh = false) => {
    // Only show loader for manual refreshes, never for background refreshes
    if (showLoader && !isBackgroundRefresh) setLoading(true);
    
    try {
      const response = await axios.get(buildApiUrl("/api/posts"));
      
      if (response.status === 200) {
        // ✅ Handle both old format (array) and new format (object with posts array)
        let fetchedPosts = [];
        
        if (Array.isArray(response.data)) {
          // Old format: direct array
          fetchedPosts = response.data;
        } else if (response.data && Array.isArray(response.data.posts)) {
          // New format: object with posts array
          fetchedPosts = response.data.posts;
          
          // Only show database error for manual refreshes, not background ones
          if (!isBackgroundRefresh && response.data.error === "database_unavailable") {
            setToast({
              message: "Database temporarily unavailable. Posts will be shown when service is restored.",
              type: "warning"
            });
          }
        } else {
          // Fallback: empty array
          fetchedPosts = [];
        }
        
        // Sort by timestamp (newest first)
        const sortedPosts = fetchedPosts.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        
        // Silently update posts without any UI disruption
        setPosts(sortedPosts);
        
        // Only log for manual refreshes to reduce console noise
        if (!isBackgroundRefresh) {
          console.log(`✅ Fetched ${sortedPosts.length} posts`);
        }

        // ✅ NEW: Mark relevant posts as read when loaded
        if (!isBackgroundRefresh && sortedPosts.length > 0) {
          markRelevantPostsAsRead(sortedPosts);
        }
      }
    } catch (error) {
      // Only show errors for manual refreshes, silently handle background refresh errors
      if (!isBackgroundRefresh) {
        console.error("❌ Error fetching posts:", error);
        setToast({
          message: "Failed to load posts. Please try again.",
          type: "error"
        });
      }
    } finally {
      // Only clear loading state if it was set (for manual refreshes)
      if (showLoader && !isBackgroundRefresh) setLoading(false);
    }
  };



  // Function to manually mark all posts as read
  const markAllAsRead = () => {
    localStorage.setItem(`postsLastViewed_${studentForm}`, new Date().toISOString());
    setHasNewPosts(false);
  };

  // Add this helper function in SchoolPostsTab.js:
  const getCategoryIcon = (category) => {
    const categoryIcons = {
      "school-announcement": "📢",
      "school-events": "🎉",
      "trips": "🚌",
      "weekly-letters": "📝"
    };
    return categoryIcons[category] || "📝";
  };

  const getCategoryLabel = (category) => {
    const categoryLabels = {
      "school-announcement": "School Announcement",
      "school-events": "School Events", 
      "trips": "Trips",
      "weekly-letters": "Weekly Letters"
    };
    return categoryLabels[category] || "Post";
  };

  // ✅ Helper function to clean filename (remove timestamp/hash prefix and decode URL)
  const cleanFileName = (filename) => {
    // Remove the timestamp_hash_ prefix (e.g., "1762767962_48ef8443_")
    // The pattern matches: digits_hexadecimal_
    let cleaned = filename.replace(/^\d+_[a-fA-F0-9]+_/, '');
    // Decode URL encoding (e.g., %20 -> space)
    try {
      cleaned = decodeURIComponent(cleaned);
    } catch (e) {
      // If decoding fails, return as-is
      console.warn('Failed to decode filename:', filename);
    }
    return cleaned;
  };

  // ✅ Helper functions for file attachments with improved icons
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': 
        return <i className="fa-solid fa-file-pdf text-red-600 text-2xl"></i>;
      case 'doc':
      case 'docx': 
        return <i className="fa-solid fa-file-word text-blue-600 text-2xl"></i>;
      case 'xls':
      case 'xlsx': 
        return <i className="fa-solid fa-file-excel text-green-600 text-2xl"></i>;
      case 'ppt':
      case 'pptx': 
        return <i className="fa-solid fa-file-powerpoint text-orange-600 text-2xl"></i>;
      case 'txt': 
        return <i className="fa-solid fa-file-lines text-gray-600 text-2xl"></i>;
      case 'zip':
      case 'rar':
      case '7z': 
        return <i className="fa-solid fa-file-zipper text-purple-600 text-2xl"></i>;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp': 
        return '🖼️'; // Image icon
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv': 
        return '🎬'; // Video icon
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac': 
        return '🎵'; // Audio icon
      case 'html':
      case 'htm': 
        return '🌐'; // Web page icon
      case 'css': 
        return '🎨'; // CSS file icon
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx': 
        return '⚡'; // JavaScript/TypeScript icon
      case 'json': 
        return '�'; // JSON file icon
      case 'xml': 
        return '📊'; // XML file icon
      case 'csv': 
        return '�'; // CSV file icon
      default: 
        return <i className="fa-solid fa-file text-gray-600 text-2xl"></i>;
    }
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'PDF Document';
      case 'doc':
      case 'docx': return 'Word Document';
      case 'xls':
      case 'xlsx': return 'Excel Spreadsheet';
      case 'ppt':
      case 'pptx': return 'PowerPoint Presentation';
      case 'txt': return 'Text File';
      case 'zip':
      case 'rar':
      case '7z': return 'Archive File';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp': return 'Image File';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv': return 'Video File';
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac': return 'Audio File';
      case 'html':
      case 'htm': return 'Web Page';
      case 'css': return 'Stylesheet';
      case 'js':
      case 'jsx': return 'JavaScript File';
      case 'ts':
      case 'tsx': return 'TypeScript File';
      case 'json': return 'JSON Data';
      case 'xml': return 'XML Document';
      case 'csv': return 'CSV Spreadsheet';
      default: return 'File';
    }
  };

  // ✅ NEW: Image modal functions
  const openImageModal = (imageUrl, allImages = [], startIndex = 0) => {
    const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : buildApiUrl(imageUrl);
    setModalImage(fullImageUrl);
    setModalImages(allImages.map(url => url.startsWith('http') ? url : buildApiUrl(url)));
    setModalImageIndex(startIndex);
    setModalImageLoading(true);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImage("");
    setModalImages([]);
    setModalImageIndex(0);
    setModalImageLoading(true);
  };

  const navigateImage = (direction) => {
    if (modalImages.length <= 1) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = modalImageIndex + 1 >= modalImages.length ? 0 : modalImageIndex + 1;
    } else {
      newIndex = modalImageIndex - 1 < 0 ? modalImages.length - 1 : modalImageIndex - 1;
    }
    
    setModalImageIndex(newIndex);
    setModalImage(modalImages[newIndex]);
    setModalImageLoading(true);
  };



  // ✅ Handle share button click
  const handleShare = async (post) => {
    try {
      // Generate shareable link
      const shareUrl = `${window.location.origin}/share/${post.id}`;
      
      // Build share text with image reference
      let shareText = `${post.title}\n\n${post.content}`;
      
      // Add image info if available
      if (post.media_urls && post.media_urls.length > 0) {
        shareText += `\n\n📷 This post includes ${post.media_urls.length} image${post.media_urls.length > 1 ? 's' : ''}`;
      }
      
      if (navigator.share) {
        await navigator.share({
          title: `${post.title} - School Portal`,
          text: shareText,
          url: shareUrl
        });
        showNotification("📤 Post shared successfully!");
      } else {
        // Fallback: copy rich text to clipboard
        const richShareText = `${post.title}\n\n${post.content}\n\n🔗 ${shareUrl}`;
        await navigator.clipboard.writeText(richShareText);
        showNotification("📋 Share link copied to clipboard!");
      }
      console.log(`Shared post ${post.id} with URL: ${shareUrl}`);
    } catch (error) {
      console.error('Error sharing post:', error);
      // Final fallback: show prompt
      const shareUrl = `${window.location.origin}/share/${post.id}`;
      prompt('Copy this link to share:', shareUrl);
      showNotification("📤 Share dialog opened");
    }
  };

  // ✅ NEW: Generate public share link
  const generatePublicShareLink = async (post) => {
    try {
      const response = await axios.get(buildApiUrl(`/api/posts/share/${post.id}`));
      
      if (response.status === 200) {
        const shareUrl = `${window.location.origin}/share/${post.id}`;
        
        // Show share options modal or copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        showNotification("🔗 Public share link copied to clipboard!");
        
        console.log("Generated share link:", response.data);
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      showNotification("❌ Error generating share link", "error");
    }
  };

  // ✅ NEW: Translate post content
  const translatePost = async (post, targetLang = 'ar') => {
    // If already translated to this language, toggle back to original
    if (translatedPosts[post.id]?.language === targetLang) {
      setTranslatedPosts(prev => {
        const newState = { ...prev };
        delete newState[post.id];
        return newState;
      });
      showNotification(`✅ Showing original text`);
      return;
    }

    try {
      setTranslatingPosts(prev => new Set([...prev, post.id]));
      
      // Use Google Translate API (free tier)
      const translateText = async (text, target) => {
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(text)}`;
        const response = await fetch(url);
        const data = await response.json();
        return data[0].map(item => item[0]).join('');
      };

      const translatedTitle = await translateText(post.title, targetLang);
      const translatedContent = await translateText(post.content.replace(/<[^>]*>/g, ''), targetLang); // Strip HTML for translation
      
      setTranslatedPosts(prev => ({
        ...prev,
        [post.id]: {
          title: translatedTitle,
          content: translatedContent,
          language: targetLang,
          languageName: targetLang === 'ar' ? 'Arabic' : targetLang === 'en' ? 'English' : targetLang
        }
      }));
      
      showNotification(`🌐 Translated to ${targetLang === 'ar' ? 'Arabic' : 'English'}`);
    } catch (error) {
      console.error('Translation error:', error);
      showNotification("❌ Translation failed. Please try again.", "error");
    } finally {
      setTranslatingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(post.id);
        return newSet;
      });
    }
  };

  // ✅ Update tab labels for teachers
  const getTabLabel = (tabType) => {
    if (role === "teacher") {
      if (tabType === "school") return "School-wide Posts";
      if (tabType === "form") return "My Classes";
      if (tabType === "me") return "About Me";
    }
    if (tabType === "school") return "Whole School";
    if (tabType === "form") return `My Class (${studentForm})`;
    if (tabType === "me") return "Me";
    return tabType;
  };

  // ✅ UPDATE: Tab content counts for proper display
  const getTabCount = (tabType) => {
    if (tabType === "school") {
      // School tab - count posts with no target_forms (school-wide)
      return posts.filter(p => (p.target_forms || []).length === 0).length;
    } else if (tabType === "form") {
      // Form tab - count based on user role
      if (role === "teacher") {
        // ✅ Teachers see count of posts for THEIR assigned forms only
        return posts.filter(p => 
          p.target_forms && 
          p.target_forms.length > 0 &&
          p.target_forms.some(form => teacherAssignedForms.includes(form))
        ).length;
      } else {
        // ✅ Students/Parents see count of posts for their form ONLY (exclude tagged posts)
        return posts.filter(p => {
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return matchesForm;
        }).length;
      }
    } else if (tabType === "me") {
      // Me tab - count posts where user is specifically tagged
      return posts.filter(p => p.tagged_pupils && p.tagged_pupils.includes(studentId)).length;
    }
    return 0;
  };

  // Add this function to SchoolPostsTab component:
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) {
      return;
    }

    try {
      const response = await axios.delete(buildApiUrl(`/api/posts/${postId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}` // If using auth tokens
        }
      });

      if (response.status === 200) {
        showNotification("✅ Post deleted successfully");
        // Optimistic update: remove from local state without re-fetching
        setPosts(prev => prev.filter(p => p.id !== postId));
        
        // ✅ FIX: Also remove from saved posts if it was saved
        const newSavedPosts = new Set(savedPosts);
        newSavedPosts.delete(postId);
        setSavedPosts(newSavedPosts);
        
        // Update localStorage to persist the change
        try {
          localStorage.setItem('savedPosts', JSON.stringify(Array.from(newSavedPosts)));
        } catch (e) {
          console.error("Error updating saved posts in localStorage:", e);
        }
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      
      if (error.response?.status === 403) {
        showNotification("❌ You can only delete your own posts!", "error");
      } else if (error.response?.status === 404) {
        showNotification("❌ Post not found!", "error");
      } else {
        showNotification("❌ Error deleting post!", "error");
      }
    }
  };

  // ✅ NEW: Handle edit post
  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowEditPost(true);
  };

  // ✅ NEW: Handle post update after edit
  const handlePostUpdated = (updatedPost) => {
    console.log("🔍 handlePostUpdated received:", updatedPost);
    console.log("🔍 Original posts count:", posts.length);
    const newPosts = posts.map(p => p.id === updatedPost.id ? updatedPost : p);
    console.log("🔍 New posts count:", newPosts.length);
    console.log("🔍 Updated post in array:", newPosts.find(p => p.id === updatedPost.id));
    setPosts(newPosts);
    setShowEditPost(false);
    setEditingPost(null);
    showNotification("✅ Post updated successfully");
  };

  // ✅ NEW: Load saved posts from localStorage on mount
  useEffect(() => {
    const storageKey = `savedPosts_${currentUser.email || currentUser.id}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const savedArray = JSON.parse(saved);
        setSavedPosts(new Set(savedArray));
      } catch (e) {
        console.error("Error loading saved posts:", e);
      }
    }
  }, [currentUser]);

  // ✅ NEW: Save/unsave post functionality
  const toggleSavePost = (postId) => {
    const storageKey = `savedPosts_${currentUser.email || currentUser.id}`;
    const newSavedPosts = new Set(savedPosts);
    
    if (newSavedPosts.has(postId)) {
      // Unsave
      newSavedPosts.delete(postId);
      showNotification("📌 Post removed from saved", "info");
    } else {
      // Save
      newSavedPosts.add(postId);
      showNotification("⭐ Post saved for later!", "success");
    }
    
    setSavedPosts(newSavedPosts);
    localStorage.setItem(storageKey, JSON.stringify([...newSavedPosts]));
  };

  // Add click outside handler to close menu:
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsMenu && !event.target.closest('.relative')) {
        setShowOptionsMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showOptionsMenu]);

  // ✅ RTL detection function
  const detectRTL = (text) => {
    // Basic Arabic/Hebrew detection
    return /[\u0600-\u06FF\u0590-\u05FF]/.test(text);
  };

  // ✅ Function to fetch post counters (batched to avoid N state updates)
  const fetchPostCountersBatch = (postsList) => {
    const newCounters = {};
    let hasNew = false;
    postsList.forEach(post => {
      if (!postCounters[post.id]) {
        hasNew = true;
        newCounters[post.id] = {
          like_count: 0,
          comment_count: 0,
          share_count: 0
        };
      }
    });
    if (hasNew) {
      setPostCounters(prev => ({ ...prev, ...newCounters }));
    }
    // TODO: Replace with actual API call to batch-fetch counters
  };

  // ✅ Fetch counters for all posts when posts load (batched, single state update)
  const postIdsKey = React.useMemo(() => posts.map(p => p.id).join(','), [posts]);
  useEffect(() => {
    if (posts.length > 0) {
      fetchPostCountersBatch(posts);
    }
  }, [postIdsKey]);

  // Fetch posts on mount and when tab/form changes; no polling interval
  useEffect(() => {
    fetchPosts(true, false);
  }, [activeTab, studentForm]);

  // ✅ Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-button-wrapper')) {
        setShowShareMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  // ✅ ADD: Filter posts based on current tab and user role
  const getFilteredPosts = () => {
    let filtered = [];
    
    // Step 1: Filter by Feed Tab (independent of category filters)
    if (activeTab === "school") {
      // "All Posts" view - show both school-wide AND form posts
      if (role === "teacher") {
        filtered = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const isForTeacherForm = p.target_forms && p.target_forms.length > 0 && 
                                    p.target_forms.some(form => teacherAssignedForms.includes(form));
          return isSchoolWide || isForTeacherForm;
        });
      } else {
        // Students/Parents see school-wide + their form posts
        filtered = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return isSchoolWide || matchesForm;
        });
      }
    } else if (activeTab === "school-wide") {
      // "School-wide Posts" view - show ONLY school-wide posts (no form posts)
      filtered = posts.filter(p => (p.target_forms || []).length === 0);
    } else if (activeTab === "me") {
      // Me tab - show posts where user is specifically tagged
      filtered = posts.filter(p => p.tagged_pupils && p.tagged_pupils.includes(studentId));
    } else if (activeTab === "saved") {
      // Saved tab - show posts that user has saved
      filtered = posts.filter(p => savedPosts.has(p.id));
    } else {
      // Form tab - filter based on user role
      if (role === "teacher") {
        // Teachers see posts for THEIR assigned forms only
        filtered = posts.filter(p => {
          const hasTargetForms = p.target_forms && p.target_forms.length > 0;
          const matchesAssignedForm = hasTargetForms && p.target_forms.some(form => teacherAssignedForms.includes(form));
          
          return hasTargetForms && matchesAssignedForm;
        });
      } else {
        // Students/Parents see posts for their form ONLY (not tagged posts)
        filtered = posts.filter(p => {
          // Show only if post targets their form (exclude tagged posts)
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return matchesForm;
        });
      }
    }

    // Step 2: Apply category filter (independent of Feed tab)
    if (activeCategory !== "all" && activeTab !== "saved") {
      if (activeCategory === "saved") {
        filtered = filtered.filter(p => savedPosts.has(p.id));
      } else {
        filtered = filtered.filter(p => p.category === activeCategory);
      }
    }

    // Step 3: Apply keyword search filter
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(keyword) ||
        p.content?.toLowerCase().includes(keyword)
      );
    }

    // Step 4: Apply teacher filter
    if (selectedTeacher !== "all") {
      filtered = filtered.filter(p => 
        p.author_email === selectedTeacher
      );
    }

    return filtered;
  };

  // ✅ NEW: Helper function to get posts by category for counts
  const getFilteredPostsByCategory = (category) => {
    // ✅ FIX: For "saved" category, always show ALL saved posts (not filtered by activeTab)
    if (category === "saved") {
      let savedFilteredPosts = posts.filter(p => savedPosts.has(p.id));
      
      // Apply keyword search
      if (searchKeyword.trim()) {
        const keyword = searchKeyword.toLowerCase();
        savedFilteredPosts = savedFilteredPosts.filter(p => 
          p.title?.toLowerCase().includes(keyword) ||
          p.content?.toLowerCase().includes(keyword)
        );
      }
      
      // Apply teacher filter
      if (selectedTeacher !== "all") {
        savedFilteredPosts = savedFilteredPosts.filter(p => 
          p.author_email === selectedTeacher
        );
      }
      
      return savedFilteredPosts;
    }
    
    // Step 1: Filter by activeTab (Feed selection) to get base posts
    let basePosts = [];
    
    if (activeTab === "school") {
      // "All Posts" view - show both school-wide AND form posts
      if (role === "teacher") {
        basePosts = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const isForTeacherForm = p.target_forms && p.target_forms.length > 0 && 
                                    p.target_forms.some(form => teacherAssignedForms.includes(form));
          return isSchoolWide || isForTeacherForm;
        });
      } else {
        basePosts = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return isSchoolWide || matchesForm;
        });
      }
    } else if (activeTab === "school-wide") {
      // "School-wide Posts" view - show ONLY school-wide posts
      basePosts = posts.filter(p => (p.target_forms || []).length === 0);
    } else {
      // Form tab - only form-specific posts
      if (role === "teacher") {
        basePosts = posts.filter(p => 
          p.target_forms && 
          p.target_forms.length > 0 &&
          p.target_forms.some(form => teacherAssignedForms.includes(form))
        );
      } else {
        // ✅ Students/Parents see posts for their form ONLY (exclude tagged posts for category filtering)
        basePosts = posts.filter(p => {
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return matchesForm;
        });
      }
    }
    
    // Step 2: Apply category filter to basePosts
    if (category !== "all") {
      basePosts = basePosts.filter(p => p.category === category);
    }
    
    // Step 3: Apply keyword search filter
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      basePosts = basePosts.filter(p => 
        p.title?.toLowerCase().includes(keyword) ||
        p.content?.toLowerCase().includes(keyword)
      );
    }
    
    // Step 4: Apply teacher filter
    if (selectedTeacher !== "all") {
      basePosts = basePosts.filter(p => 
        p.author_email === selectedTeacher
      );
    }
    
    return basePosts;
  };

  // ✅ Helper function to get available teachers based on current filtered context
  const getAvailableTeachers = () => {
    // Get posts filtered by current tab and category (but NOT by teacher or keyword)
    let contextPosts = [];
    
    // Step 1: Filter by activeTab
    if (activeTab === "school") {
      if (role === "teacher") {
        contextPosts = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const isForTeacherForm = p.target_forms && p.target_forms.length > 0 && 
                                    p.target_forms.some(form => teacherAssignedForms.includes(form));
          return isSchoolWide || isForTeacherForm;
        });
      } else {
        contextPosts = posts.filter(p => {
          const isSchoolWide = (p.target_forms || []).length === 0;
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return isSchoolWide || matchesForm;
        });
      }
    } else if (activeTab === "school-wide") {
      contextPosts = posts.filter(p => (p.target_forms || []).length === 0);
    } else if (activeTab === "me") {
      contextPosts = posts.filter(p => p.tagged_pupils && p.tagged_pupils.includes(studentId));
    } else if (activeTab === "saved") {
      contextPosts = posts.filter(p => savedPosts.has(p.id));
    } else {
      if (role === "teacher") {
        contextPosts = posts.filter(p => {
          const hasTargetForms = p.target_forms && p.target_forms.length > 0;
          const matchesAssignedForm = hasTargetForms && p.target_forms.some(form => teacherAssignedForms.includes(form));
          return hasTargetForms && matchesAssignedForm;
        });
      } else {
        contextPosts = posts.filter(p => {
          const matchesForm = p.target_forms && p.target_forms.includes(studentForm);
          return matchesForm;
        });
      }
    }

    // Step 2: Apply category filter
    if (activeCategory !== "all" && activeTab !== "saved") {
      if (activeCategory === "saved") {
        contextPosts = contextPosts.filter(p => savedPosts.has(p.id));
      } else {
        contextPosts = contextPosts.filter(p => p.category === activeCategory);
      }
    }

    // Extract unique teacher emails from filtered posts
    return Array.from(new Set(contextPosts.map(p => p.author_email).filter(Boolean))).sort();
  };

  // ✅ MOVE this useEffect INSIDE the component (around line 400-450)
  useEffect(() => {
    if (studentId && posts.length > 0) {
      // Tagged pupils tracking (silent)
    }
  }, [posts, studentId]);

  // ... rest of your functions (getFilteredPosts, etc.) ...

  return (
    <>
      {/* Mobile Slide-out Menu Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-white z-50 overflow-y-auto lg:hidden shadow-2xl">
            <div className="p-4">
              {/* Close Button */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Filters & Navigation</h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Main Tabs */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">📂 Feed</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => { setActiveTab("school"); setIsMobileMenuOpen(false); }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all ${
                      activeTab === "school"
                        ? "bg-blue-500 text-white shadow-md"
                        : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span className="font-medium">{getTabLabel("school")}</span>
                    <span className="text-xs opacity-75">({getTabCount("school")})</span>
                  </button>
                  <button
                    onClick={() => { setActiveTab("form"); setIsMobileMenuOpen(false); }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all ${
                      activeTab === "form"
                        ? "bg-blue-500 text-white shadow-md"
                        : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span className="font-medium">{getTabLabel("form")}</span>
                    <span className="text-xs opacity-75">({getTabCount("form")})</span>
                  </button>
                  {(role === "parent" || role === "student") && (
                    <button
                      onClick={() => { setActiveTab("me"); setIsMobileMenuOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all ${
                        activeTab === "me"
                          ? "bg-blue-500 text-white shadow-md"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="font-medium">{getTabLabel("me")}</span>
                      <span className="text-xs opacity-75">({getTabCount("me")})</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">🏷️ Category</h3>
                <select
                  value={activeCategory}
                  onChange={(e) => setActiveCategory(e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[
                    { value: "all", label: "🔄 All Posts" },
                    { value: "saved", label: "🔖 My Saved Posts" },
                    { value: "school-announcement", label: "📢 Announcements" },
                    { value: "school-events", label: "🎉 Events" },
                    { value: "trips", label: "🚌 Trips" },
                    { value: "weekly-letters", label: "📝 Letters" },
                    { value: "daily-activity", label: "📅 Daily Activity" },
                  ].map(cat => {
                    const count = getFilteredPostsByCategory(cat.value).length;
                    return (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Search Filters */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-3">🔍 Search</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="🔍 Search posts..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  <select
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                    className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">👥 All Teachers</option>
                    {getAvailableTeachers().map(email => (
                      <option key={email} value={email}>
                        {getTeacherName(email)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* View Results Button - closes menu and applies filters */}
              <div className="mb-4">
                <button
                  onClick={() => {
                    console.log('📱 View Filtered Results clicked - closing menu with filters:', {
                      activeCategory,
                      searchKeyword,
                      selectedTeacher
                    });
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>View Filtered Results</span>
                </button>
              </div>

              {/* Clear Filters */}
              {(searchKeyword || selectedTeacher !== "all" || activeCategory !== "all") && (
                <button
                  onClick={() => {
                    setSearchKeyword("");
                    setSelectedTeacher("all");
                    setActiveCategory("all");
                  }}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  ✖ Clear All Filters
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop Layout */}
      <div className="flex min-h-screen bg-gray-50">
        {/* Desktop Sidebar - Fixed Width */}
        <div className="hidden lg:block w-80 bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 min-h-screen sticky top-0 overflow-y-auto">
          <div className="p-6 space-y-6">
            
            {/* Container 1: School Wide / My Classes */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🏫</span>
                <span>Feed</span>
              </h2>
              <div className="space-y-2">
                {/* All Posts Button */}
                <button
                  onClick={() => {
                    setActiveTab("school");
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    activeTab === "school"
                      ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md transform scale-[1.02]"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🔄</span>
                    <span className="font-semibold">All Posts</span>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab("school-wide");
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    activeTab === "school-wide"
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md transform scale-[1.02]"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🌐</span>
                    <span className="font-semibold">School-wide Posts</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("form")}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    activeTab === "form"
                      ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md transform scale-[1.02]"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🎓</span>
                    <span className="font-semibold">{getTabLabel("form")}</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Container 2: Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🔍</span>
                <span>Filters</span>
              </h2>
              <div className="space-y-2">
                {[
                  { value: "all", label: "All Posts", icon: "🔄", gradient: "from-purple-500 to-purple-600" },
                  { value: "saved", label: "My Saved Posts", icon: "🔖", gradient: "from-yellow-500 to-yellow-600" },
                  { value: "school-announcement", label: "Announcements", icon: "📢", gradient: "from-blue-500 to-blue-600" },
                  { value: "school-events", label: "Events", icon: "🎉", gradient: "from-pink-500 to-pink-600" },
                  { value: "trips", label: "Trips", icon: "🚌", gradient: "from-orange-500 to-orange-600" },
                  { value: "weekly-letters", label: "Letters", icon: "📝", gradient: "from-teal-500 to-teal-600" },
                  { value: "daily-activity", label: "Daily Activity", icon: "📅", gradient: "from-green-500 to-green-600" },
                ].map(cat => {
                  return (
                    <button
                      key={cat.value}
                      onClick={() => {
                        // Filters are independent from Feed tabs
                        // Just set the category filter without changing the active tab
                        setActiveCategory(cat.value);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-left transition-all duration-200 ${
                        activeCategory === cat.value
                          ? `bg-gradient-to-r ${cat.gradient} text-white shadow-md transform scale-[1.02]`
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{cat.icon}</span>
                        <span className="text-sm font-semibold">{cat.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Container 2.5: Search Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🔎</span>
                <span>Search</span>
              </h2>
              <div className="space-y-3">
                {/* Keyword Search */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Search Keywords
                  </label>
                  <input
                    type="text"
                    placeholder="🔍 Search posts..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                </div>
                
                {/* Teacher Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Filter by Teacher
                  </label>
                  <select
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                    className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="all">👥 All Teachers</option>
                    {getAvailableTeachers().map(email => (
                      <option key={email} value={email}>
                        {getTeacherName(email)}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Clear Filters Button */}
                {(searchKeyword || selectedTeacher !== "all") && (
                  <button
                    onClick={() => {
                      setSearchKeyword("");
                      setSelectedTeacher("all");
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                  >
                    ✖ Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Container 3: My Saved Posts */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">⭐</span>
                <span>Saved</span>
              </h2>
              
              {/* My Saved Posts Button */}
              <button
                onClick={() => setActiveTab("saved")}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 mb-3 ${
                  activeTab === "saved"
                    ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md transform scale-[1.02]"
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">🔖</span>
                  <span className="font-semibold">My Saved Posts</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  activeTab === "saved" 
                    ? "bg-white text-amber-600" 
                    : "bg-white text-gray-600"
                }`}>
                  {savedPosts.size}
                </span>
              </button>
              
              {(role === "parent" || role === "student") && (
                <button
                  onClick={() => setActiveTab("me")}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    activeTab === "me"
                      ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md transform scale-[1.02]"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">👤</span>
                    <span className="font-semibold">{getTabLabel("me")}</span>
                  </div>
                </button>
              )}
              <div className="mt-4 p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg text-center">
                <p className="text-xs text-gray-600 leading-relaxed">
                  📌 Your saved posts appear here for quick access
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Main Content Area - Posts */}
        <div className="flex-1">
          <div className="px-0 lg:px-8 py-6">
            {loading && posts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading amazing posts...</p>
              </div>
            ) : (
              <>
                {(() => {
                  const filteredPosts = getFilteredPosts();
                  
                  if (filteredPosts.length === 0 && !loading) {
                    return (
                      <div className="text-center py-16 bg-white rounded-3xl shadow-lg border border-gray-100">
                        <div className="text-7xl mb-6">�</div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">No Posts Found</h3>
                        <p className="text-gray-500 text-lg">
                          {activeTab === "school" 
                            ? "No school-wide posts available yet." 
                            : role === "teacher"
                              ? `No posts for your assigned classes yet.`
                              : `No posts for ${studentForm} yet.`
                          }
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="w-full md:max-w-7xl md:mx-auto space-y-2 md:space-y-6 px-0">
                      {filteredPosts.map((post, index) => (
                      <div
                        key={index}
                        className="bg-white md:rounded-2xl shadow-sm border-0 md:border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200 relative"
                      >
                        {/* Post Header */}
                        <div className="p-3 md:p-4 pb-0">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-semibold">
                                  {getTeacherName(post.created_by).charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {getTeacherName(post.created_by)}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {new Date(post.timestamp).toLocaleDateString()} • {new Date(post.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                              </div>
                            </div>
                            
                            {/* Post Options Menu */}
                            {(() => {
                              // ✅ RESTORED: Proper security - only post creator can edit/delete
                              const canEdit = currentUser.role === "teacher" && post.created_by === currentUser.email;
                              // Logging disabled - was causing thousands of logs per minute
                              return canEdit;
                            })() && (
                              <div className="relative">
                                <button
                                  onClick={() => setShowOptionsMenu(showOptionsMenu === post.id ? null : post.id)}
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                  </svg>
                                </button>
                                
                                {showOptionsMenu === post.id && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                    <button
                                      onClick={() => {
                                        handleEditPost(post);
                                        setShowOptionsMenu(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                    >
                                      <span>✏️</span>
                                      <span>Edit Post</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDeletePost(post.id);
                                        setShowOptionsMenu(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                    >
                                      <span>🗑️</span>
                                      <span>Delete Post</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <h3 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h3>
                          
                          {/* Tags - Only show target_forms to teachers */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {role === "teacher" && (
                              <>
                                {post.target_forms?.length > 0 ? (
                                  post.target_forms.map((form, i) => (
                                    <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                      🎓 {form}
                                    </span>
                                  ))
                                ) : (
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                    🌐 Whole School
                                  </span>
                                )}
                              </>
                            )}
                            
                            {/* Tagged pupils indicator */}
                            {post.tagged_pupils?.includes(studentId) && role !== "teacher" && (
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                                🔔 {role === "student" ? "You're tagged" : "Child tagged"}
                              </span>
                            )}
                            
                            {role === "teacher" && post.tagged_pupils?.length > 0 && (
                              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                                🎯 {post.tagged_pupils.length} tagged
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Videos */}
                        {post.media_urls?.length > 0 && (
                          <div className="px-3 md:px-4 pb-2">
                            {post.media_urls
                              .filter(url => url.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i))
                              .map((url, i) => {
                                const fileUrl = url.startsWith('http') ? url : buildApiUrl(url);
                                return (
                                  <div key={i} className="mb-3 flex justify-center">
                                    <video 
                                      controls 
                                      className="max-w-md w-full rounded-lg border border-gray-200"
                                      style={{ maxHeight: '400px' }}
                                      preload="metadata"
                                    >
                                      <source src={fileUrl} />
                                      Your browser does not support the video tag.
                                    </video>
                                  </div>
                                );
                              })}
                          </div>
                        )}

                        {/* PDF and file attachments */}
                        {post.media_urls?.length > 0 && (
                          <div className="px-3 md:px-4 pb-2">
                            {post.media_urls
                              .filter(url => !url.match(/\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i))
                              .map((url, i) => {
                                const rawName = url.split("/").pop() || url;
                                const displayName = cleanFileName(rawName);
                                const fileUrl = url.startsWith('http') ? url : buildApiUrl(url);
                                return (
                                  <a
                                    key={i}
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-3 mb-2 bg-white hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors group"
                                  >
                                    <span className="text-2xl">{getFileIcon(displayName)}</span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                                        {displayName}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Click to open • {getFileType(displayName)}
                                      </p>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                );
                              })}
                          </div>
                        )}

                        {/* Image Carousel */}
                        <ImageCarousel 
                          imageUrls={post.media_urls?.filter(url => url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) || []}
                          onImageClick={openImageModal}
                        />

                        {/* Content */}
                        <div className="px-3 md:px-4 pb-4">
                          {translatedPosts[post.id] ? (
                            <div className="space-y-3">
                              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                </svg>
                                <span>Translated to {translatedPosts[post.id].languageName}</span>
                              </div>
                              <div 
                                className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                style={{ direction: translatedPosts[post.id].language === 'ar' ? 'rtl' : 'ltr' }}
                              >
                                <h3 className="font-semibold text-lg mb-2">{translatedPosts[post.id].title}</h3>
                                <p>{translatedPosts[post.id].content}</p>
                              </div>
                            </div>
                          ) : (
                            <div 
                              className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: post.content }}
                            />
                          )}
                        </div>

                        {/* Interaction Bar */}
                        <div className="px-3 md:px-4 pt-3 pb-4 border-t border-gray-100 overflow-visible">
                          <div className="flex items-center justify-between overflow-visible">
                            <div className="flex items-center space-x-6 overflow-visible">
                              {/* Save/Bookmark Button */}
                              <button 
                                onClick={() => toggleSavePost(post.id)}
                                className={`flex items-center space-x-2 transition-colors ${
                                  savedPosts.has(post.id)
                                    ? "text-amber-500"
                                    : "text-gray-600 hover:text-amber-500"
                                }`}
                                title={savedPosts.has(post.id) ? "Remove from saved" : "Save for later"}
                              >
                                <svg className="w-5 h-5" fill={savedPosts.has(post.id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                </svg>
                                <span className="text-sm font-medium">
                                  {savedPosts.has(post.id) ? "Saved" : "Save"}
                                </span>
                              </button>
                              
                              <button 
                                onClick={(e) => {
                                  console.log("Share button clicked for post:", post.id);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setShareMenuPosition({
                                    top: rect.bottom + 8,
                                    left: rect.left
                                  });
                                  setShowShareMenu(showShareMenu === post.id ? null : post.id);
                                }}
                                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors px-3 py-2 rounded-lg hover:bg-gray-50 share-button-wrapper"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"></path>
                                </svg>
                                <span className="text-sm font-medium">Share</span>
                              </button>

                              {/* Translate Button */}
                              <button 
                                onClick={() => translatePost(post, translatedPosts[post.id]?.language === 'ar' ? 'en' : 'ar')}
                                disabled={translatingPosts.has(post.id)}
                                className={`flex items-center space-x-2 transition-colors ${
                                  translatedPosts[post.id]
                                    ? "text-blue-500"
                                    : "text-gray-600 hover:text-blue-500"
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                title={translatedPosts[post.id] ? `Translated to ${translatedPosts[post.id].languageName}` : "Translate"}
                              >
                                {translatingPosts.has(post.id) ? (
                                  <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                  </svg>
                                )}
                                <span className="text-sm font-medium">
                                  {translatingPosts.has(post.id) 
                                    ? "Translating..." 
                                    : translatedPosts[post.id] 
                                      ? "Original" 
                                      : "Translate"}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Share Menu - Rendered at root level with fixed positioning */}
                        {showShareMenu === post.id && (
                          <div 
                            className="fixed w-64 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-[9999] share-button-wrapper"
                            style={{
                              top: `${shareMenuPosition.top}px`,
                              left: `${shareMenuPosition.left}px`
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("Quick Share clicked for post:", post.id);
                                handleShare(post);
                                setShowShareMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                            >
                              <span>📱</span>
                              <div>
                                <div className="font-medium">Quick Share</div>
                                <div className="text-xs text-gray-500">Use device share menu or copy link</div>
                              </div>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("Public Link clicked for post:", post.id);
                                generatePublicShareLink(post);
                                setShowShareMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                            >
                              <span>🔗</span>
                              <div>
                                <div className="font-medium">Public Link</div>
                                <div className="text-xs text-gray-500">Anyone with link can view (no login required)</div>
                              </div>
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("Copy Text clicked for post:", post.id);
                                const shareText = `${post.title}\n\n${post.content}\n\nShared from School Portal`;
                                navigator.clipboard.writeText(shareText);
                                showNotification("📋 Post text copied to clipboard!");
                                setShowShareMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                            >
                              <span>📋</span>
                              <div>
                                <div className="font-medium">Copy Text</div>
                                <div className="text-xs text-gray-500">Copy post content as text</div>
                              </div>
                            </button>
                          </div>
                        )}

                      </div>
                    ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Floating Action Button for Teachers */}
      {role === "teacher" && (
        <button
          onClick={() => setShowCreatePost(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 lg:bottom-8 lg:right-8"
          title="Create New Post"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}

      {/* Modals and Overlays */}
      {showCreatePost && (
        <CreatePost
          onClose={() => setShowCreatePost(false)}
          onPostCreated={() => {
            fetchPosts(true, false); // Show loader when refreshing after creating post
            setShowCreatePost(false);
          }}
          authorInfo={authorInfo}
          studentForm={studentForm}
          targetForms={targetForms}
        />
      )}

      {showEditPost && editingPost && (
        <EditPost
          post={editingPost}
          onClose={() => {
            setShowEditPost(false);
            setEditingPost(null);
          }}
          onPostUpdated={handlePostUpdated}
          authorInfo={authorInfo}
          studentForm={studentForm}
          targetForms={targetForms}
        />
      )}

      {/* Toast Notification */}
      {toast.message && (
        <div className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300 ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.message}
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={closeImageModal}>
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold z-10"
            >
              ✕
            </button>

            {/* Navigation buttons */}
            {modalImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('prev');
                  }}
                  className="absolute left-4 text-white hover:text-gray-300 text-4xl font-bold z-10"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('next');
                  }}
                  className="absolute right-4 text-white hover:text-gray-300 text-4xl font-bold z-10"
                >
                  ›
                </button>
              </>
            )}

            {/* Image counter */}
            {modalImages.length > 1 && (
              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                {modalImageIndex + 1} / {modalImages.length}
              </div>
            )}

            {/* Main image */}
            <div className="relative flex items-center justify-center w-full h-full">
              {/* Loading Spinner */}
              {modalImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/30 border-t-white"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl">📸</span>
                    </div>
                  </div>
                </div>
              )}

              <img
                src={modalImage}
                alt="Enlarged view"
                className={`max-w-full max-h-full object-contain ${modalImageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Keyboard navigation hint */}
            {modalImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                Use ← → keys or click arrows to navigate
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ✅ Helper functions go OUTSIDE the component but BEFORE export
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'pdf': 
      return '📄'; // PDF icon
    case 'doc':
    case 'docx': 
      return '📝'; // Word document icon
    case 'xls':
    case 'xlsx': 
      return '📊'; // Excel spreadsheet icon
    case 'ppt':
    case 'pptx': 
      return '📺'; // PowerPoint presentation icon
    case 'txt': 
      return '📃'; // Text file icon
    case 'zip':
    case 'rar':
    case '7z': 
      return '🗜️'; // Archive icon
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'bmp': 
      return '🖼️'; // Image icon
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'mkv': 
      return '🎬'; // Video icon
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg': 
      return '🎵'; // Audio icon
    case 'html':
    case 'htm': 
      return '🌐'; // Web page icon
    case 'css': 
      return '�'; // CSS file icon
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx': 
      return '⚡'; // JavaScript/TypeScript icon
    case 'json': 
      return '📋'; // JSON file icon
    case 'xml': 
      return '📊'; // XML file icon
    case 'csv': 
      return '�'; // CSV file icon
    default: 
      return '📎'; // Generic file icon
  }
};

const getFileType = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  const typeMap = {
    'pdf': 'PDF Document',
    'doc': 'Word Document',
    'docx': 'Word Document',
    'xls': 'Excel Spreadsheet',
    'xlsx': 'Excel Spreadsheet',
    'ppt': 'PowerPoint Presentation',
    'pptx': 'PowerPoint Presentation',
    'txt': 'Text File',
    'zip': 'Archive File',
    'rar': 'Archive File',
    '7z': 'Archive File',
    'mp3': 'Audio File',
    'mp4': 'Video File',
    'avi': 'Video File',
    'mov': 'Video File',
    'wmv': 'Video File',
    'flv': 'Video File',
    'mkv': 'Video File',
    'wav': 'Audio File',
    'flac': 'Audio File',
    'aac': 'Audio File',
    'ogg': 'Audio File',
    'jpg': 'Image File',
    'jpeg': 'Image File',
    'png': 'Image File',
    'gif': 'Image File',
    'webp': 'Image File',
    'bmp': 'Image File',
    'html': 'Web Page',
    'htm': 'Web Page',
    'css': 'Stylesheet',
    'js': 'JavaScript File',
    'jsx': 'JavaScript File',
    'ts': 'TypeScript File',
    'tsx': 'TypeScript File',
    'json': 'JSON Data',
    'xml': 'XML Document',
    'csv': 'CSV Spreadsheet'
  };
  
  return typeMap[ext] || 'File';
};

// ✅ These should also be OUTSIDE the component
const categoryColors = {
  "school-announcement": "bg-blue-100 text-blue-800",
  "school-events": "bg-green-100 text-green-800",
  "trips": "bg-yellow-100 text-yellow-800",
  "weekly-letters": "bg-purple-100 text-purple-800"
};

const categoryLabels = {
  "school-announcement": "School Announcement",
  "school-events": "School Events",
  "trips": "Trips",
  "weekly-letters": "Weekly Letters"
};

export default SchoolPostsTab;
